import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useGuidedTour } from '@/context/GuidedTourContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OnboardingWizard — auto-launches the Joyride "getting-started" guided tour
 * on a user's first authenticated session. Replaces the legacy custom overlay;
 * we now have a single source of truth for tours (lib/guidedTours.js).
 *
 * Behaviour:
 *  1. On mount (once we have a token + user), GET /api/onboarding.
 *  2. If `completed === false` AND no tour is already running, fire
 *     `startTour('getting-started')`.
 *  3. Immediately PUT completed=true so the tour doesn't re-launch on every
 *     subsequent page visit. Users can re-take it anytime from /training.
 */
const OnboardingWizard = () => {
  const { token, user } = useAuth();
  const { startTour, activeTourId } = useGuidedTour();
  const launched = useRef(false);

  useEffect(() => {
    if (!token || !user || launched.current) return;

    const run = async () => {
      try {
        const res = await axios.get(`${API}/onboarding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.completed) return;
      } catch {
        // Non-fatal — treat as "not completed" and continue.
      }

      if (activeTourId) return;

      launched.current = true;
      startTour('getting-started');

      try {
        await axios.put(
          `${API}/onboarding`,
          { completed: true, current_step: 0 },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch { /* silent */ }
    };

    run();
  }, [token, user, activeTourId, startTour]);

  return null;
};

export default OnboardingWizard;
